import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { subscribeToPush } from '@/lib/pushPermissions';
import { apiUrl } from '@/lib/api-url';
import { firstSwipePushTitle } from '@/lib/firstSwipeCopy';
import { isIosGateOnCooldown } from '@/lib/iosGateCooldown';
// Track 1 (parallel branch): in-app browser detector. Used here to skip
// modal rendering inside FB/IG/TikTok WebViews where push silently fails.
// The Track 1 overlay (`<InAppBrowserOverlay />`, mounted at App root)
// handles the user-facing "Open in real browser" message. Import may
// resolve red until Track 1 lands; merge will reconcile.
import { detectInAppBrowser } from '@/lib/inAppBrowser';
import {
  type PushPromptVariant,
  type BehavioralContext,
  detectPlatform,
  fetchBehavioralContext,
  flushPromptEventQueue,
  isInstalledPWA,
  logPromptEvent,
} from '@/lib/pushPromptAnalytics';

const SESSION_START_KEY = 'papacito_session_start_at';
const SESSION_PERMISSION_PROBED_KEY = 'papacito_session_permission_probed';
// 2026-05 cooldown rebuild: the 24h/7d per-variant cooldowns and the
// 2-prompts-per-session cap accounted for 1,144 of 4,395 gate-skips in 7 days
// of prod data (979 cooldown_active + 165 max_prompts_per_session). Both are
// replaced by a single 5-min global floor that's bypassed when a different
// real event (different match, different unread count, different swipe count)
// supplies a new context key — re-prompts are now event-driven, not timer-
// driven, and same-context retries are throttled rather than blocked outright.
const MIN_MS_BETWEEN_PROMPTS = 5 * 60 * 1000;
const SESSION_LAST_PROMPT_AT_KEY = 'papacito_session_last_prompt_at';
const SESSION_LAST_CONTEXT_KEY = 'papacito_session_last_prompt_context';
const MIN_MS_AFTER_SESSION_START = 10 * 1000;
// Variant D "Welcome" — fallback fallback for fresh accounts that have no
// likes / matches / views yet. Requires the user to have sat on the app for
// at least 15s after session start before we nudge.
const VARIANT_D_MIN_SESSION_MS = 15 * 1000;

// Default variant slug used for "ambient" diagnostic events that aren't tied
// to a specific variant trigger (permission_state_at_check, ios_install_gate_*
// when fired before any variant is in flight). The endpoint validates `variant`
// against a strict enum, so we can't use 'unknown' or 'global' — D_welcome is
// the closest semantic fit because it's the global fallback variant. Dashboards
// should slice on `eventType`, not `variant`, when reading these rows.
const AMBIENT_VARIANT: PushPromptVariant = 'D_welcome';

type ExtendedEventType =
  | 'gate_skipped'
  | 'subscribe_failed'
  | 'permission_state_at_check'
  | 'ios_install_gate_shown'
  | 'ios_install_gate_accepted'
  | 'ios_install_gate_dismissed';

type PermissionStateLabel = 'default' | 'granted' | 'denied' | 'unsupported';

function readPermissionState(): PermissionStateLabel {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  try {
    const p = Notification.permission;
    if (p === 'granted' || p === 'denied' || p === 'default') return p;
    return 'unsupported';
  } catch {
    return 'unsupported';
  }
}

/**
 * Fire-and-forget POST for the diagnostic event types added 2026-05-01 to
 * debug 0% push opt-in. Posts directly to the prompt-event endpoint so we can
 * pass the new `gateReason` / `permissionState` fields without bending the
 * shared `logPromptEvent` signature. Silently swallows ALL failures — if
 * Track 1's backend expansion isn't deployed yet, the endpoint will 400 on
 * the new eventType and that's fine. Never crashes the user UX.
 */
async function postExtendedEvent(
  token: string | null | undefined,
  variant: PushPromptVariant,
  eventType: ExtendedEventType,
  opts: {
    gateReason?: string;
    permissionState?: PermissionStateLabel;
    contextMetadata?: Record<string, unknown> | null;
  } = {},
): Promise<void> {
  if (!token) return;
  try {
    await fetch(apiUrl('/api/push/prompt-event'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        variant,
        eventType,
        platform: detectPlatform(),
        isInstalled: isInstalledPWA(),
        contextMetadata: opts.contextMetadata ?? null,
        gateReason: opts.gateReason ?? null,
        permissionState: opts.permissionState ?? readPermissionState(),
      }),
      // keepalive lets us fire diagnostic events on flows that may unmount
      // immediately after (e.g. iOS gate accept → navigate).
      keepalive: true,
    });
  } catch {
    /* swallow — diagnostics must never break UX */
  }
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function getSessionStartMs(): number {
  try {
    const v = sessionStorage.getItem(SESSION_START_KEY);
    if (v) return Number(v);
    const now = Date.now();
    sessionStorage.setItem(SESSION_START_KEY, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

function readNum(key: string): number {
  try { return Number(sessionStorage.getItem(key) || '0'); } catch { return 0; }
}
function readStr(key: string): string {
  try { return sessionStorage.getItem(key) || ''; } catch { return ''; }
}
function writeStr(key: string, value: string) {
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
}
// Records the moment a prompt was shown along with its context key. Used by
// canShowAnyPrompt to enforce the 5-min global floor while letting a fresh
// context (different match, different unread count, etc.) bypass it.
function markPromptShown(contextKey: string) {
  writeStr(SESSION_LAST_PROMPT_AT_KEY, String(Date.now()));
  writeStr(SESSION_LAST_CONTEXT_KEY, contextKey);
}

function hasProbedPermissionThisSession(): boolean {
  try { return sessionStorage.getItem(SESSION_PERMISSION_PROBED_KEY) === '1'; } catch { return false; }
}
function markPermissionProbed() {
  try { sessionStorage.setItem(SESSION_PERMISSION_PROBED_KEY, '1'); } catch { /* ignore */ }
}

// iOS install gate (IOSInstallGateModal) cooldown. The gate substitutes for the
// push prompt on iOS Safari; without a cooldown it re-shows on every behavioral
// variant trigger (first swipe, the 16s timer, etc.). Persisted in localStorage
// so the 10-min window survives reloads. Pure decision: lib/iosGateCooldown.ts.
const IOS_GATE_LAST_SHOWN_KEY = 'papacito_ios_gate_last_shown_at';
function readIosGateLastShown(): number | null {
  try {
    const v = localStorage.getItem(IOS_GATE_LAST_SHOWN_KEY);
    return v ? Number(v) : null;
  } catch {
    return null;
  }
}
function markIosGateShown() {
  try { localStorage.setItem(IOS_GATE_LAST_SHOWN_KEY, String(Date.now())); } catch { /* ignore */ }
}

export interface BehavioralPromptState {
  visible: boolean;
  variant: PushPromptVariant | null;
  loading: boolean;
  blurredPhotoUrl: string | null;
  iosGateVisible: boolean;
  contextMetadata: Record<string, unknown> | null;
  triggerVariantA: () => Promise<void>;
  triggerVariantB: (matchId?: number | string) => Promise<void>;
  triggerVariantC: () => Promise<void>;
  triggerVariantD: () => Promise<void>;
  triggerVariantE: () => Promise<void>;
  triggerVariantF: (direction: 'like' | 'pass') => Promise<void>;
  accept: () => Promise<void>;
  decline: () => Promise<void>;
  dismissIosGate: () => void;
  showIosGuide: () => void;
  refreshContext: () => Promise<void>;
}

export function useBehavioralPushOptIn(): BehavioralPromptState {
  const { token, user, refreshUser } = useAuth();
  const [visible, setVisible] = useState(false);
  const [variant, setVariant] = useState<PushPromptVariant | null>(null);
  const [loading, setLoading] = useState(false);
  const [blurredPhotoUrl, setBlurredPhotoUrl] = useState<string | null>(null);
  const [iosGateVisible, setIosGateVisible] = useState(false);
  const [contextMetadata, setContextMetadata] = useState<Record<string, unknown> | null>(null);
  const contextRef = useRef<BehavioralContext | null>(null);
  const visibleRef = useRef(false);
  const shownRef = useRef<Record<string, boolean>>({});
  const decliningRef = useRef(false);
  // Tracks which variant is queued behind the iOS install gate so we can
  // attribute ios_install_gate_shown back to its trigger.
  const iosGateForVariantRef = useRef<PushPromptVariant | null>(null);

  useEffect(() => { visibleRef.current = visible; }, [visible]);

  const refreshContext = useCallback(async () => {
    if (!token) return;
    const ctx = await fetchBehavioralContext(token);
    if (ctx) contextRef.current = ctx;
  }, [token]);

  // Initial context load + flush queued events + one-shot permission probe.
  // The probe is the single most useful diagnostic for the 0% opt-in
  // investigation: it tells us, per session, what state Notification.permission
  // is in BEFORE any prompt logic runs. A high "denied" count would explain
  // why nothing fires for returning users; a high "default" count means we
  // really are dropping prompts on eligible users.
  useEffect(() => {
    if (!token) return;
    flushPromptEventQueue(token);
    refreshContext();
    getSessionStartMs();
    if (!hasProbedPermissionThisSession()) {
      markPermissionProbed();
      void postExtendedEvent(token, AMBIENT_VARIANT, 'permission_state_at_check', {
        permissionState: readPermissionState(),
        contextMetadata: {
          standalone: isStandalone(),
          ios: isIOS(),
        },
      });
    }
  }, [token, refreshContext]);

  /**
   * Returns true when the current state allows a new prompt to surface. After
   * every `return false` we fire a `gate_skipped` event so we can finally see
   * WHY 11/15 fresh signups never get a prompt. Each branch has a unique
   * `gateReason`. Pass `intendedVariant` so dashboards can filter by which
   * variant was being attempted; falls back to D_welcome (the global fallback
   * variant) for context-less callers.
   */
  const canShowAnyPrompt = useCallback(
    (
      intendedVariant: PushPromptVariant = AMBIENT_VARIANT,
      contextKey: string | null = null,
      opts: { skipSessionStartGuard?: boolean } = {},
    ): boolean => {
      if (typeof window === 'undefined') return false;
      const fire = (gateReason: string) => {
        void postExtendedEvent(token, intendedVariant, 'gate_skipped', {
          gateReason,
          permissionState: readPermissionState(),
        });
      };
      // 2026-05-01 Track 1 (push-funnel-fixes-2): in-app browser short-circuit
      // MUST run before the `no_notification_api` check. FB/IG/TikTok WebViews
      // strip `Notification` from `window`, so they were being misattributed
      // to `no_notification_api` (165 events / 15 users since PR #151 deploy)
      // when the real cause was the in-app browser. Catching this first gives
      // correct attribution + lets the `<InAppBrowserOverlay />` (mounted at
      // App root) handle the user-facing "Open in real browser" CTA.
      const inApp = detectInAppBrowser();
      if (inApp) {
        fire(`inapp_browser_${inApp}`);
        return false;
      }
      if (!('Notification' in window)) {
        fire('no_notification_api');
        return false;
      }
      if (Notification.permission === 'denied') {
        fire('permission_already_denied');
        return false;
      }
      if (Notification.permission === 'granted') {
        fire('permission_already_granted');
        return false;
      }
      const ctx = contextRef.current;
      if (ctx?.hasPushSubscription) {
        fire('has_active_subscription');
        return false;
      }
      if (visibleRef.current) {
        fire('modal_already_visible');
        return false;
      }
      if (iosGateVisible) {
        fire('ios_gate_already_visible');
        return false;
      }
      // Variant F (first-swipe chase) bypasses this guard: the swipe itself is
      // the engagement signal, so a fast swiper should not slip the chase.
      if (
        !opts.skipSessionStartGuard &&
        Date.now() - getSessionStartMs() < MIN_MS_AFTER_SESSION_START
      ) {
        fire('min_session_age_not_met');
        return false;
      }
      // 5-min global floor. Bypassed when contextKey differs from the last
      // prompt's contextKey — a different real event (new match, new unread
      // message, fresh swipe count) gets its own pitch, not a recycled one.
      // Caller passes null to opt out of the floor entirely (rare).
      if (contextKey !== null) {
        const lastAt = readNum(SESSION_LAST_PROMPT_AT_KEY);
        const lastContext = readStr(SESSION_LAST_CONTEXT_KEY);
        if (
          lastAt > 0 &&
          Date.now() - lastAt < MIN_MS_BETWEEN_PROMPTS &&
          lastContext === contextKey
        ) {
          // Keep the gate_reason name 'cooldown_active' for dashboard
          // continuity with the prior 24h/7d cooldown system.
          fire('cooldown_active');
          return false;
        }
      }
      return true;
    },
    [token, iosGateVisible],
  );

  const maybeShowIosGate = useCallback(
    (intendedVariant: PushPromptVariant = AMBIENT_VARIANT): boolean => {
      if (isIOS() && !isStandalone()) {
        // 10-min cooldown. The gate substitutes for the push prompt on iOS, so
        // when it's on cooldown we suppress it AND return true — that stops
        // showVariant from falling through and rendering the push modal (which
        // is useless on iOS Safari) in its place.
        if (isIosGateOnCooldown(readIosGateLastShown(), Date.now())) {
          void postExtendedEvent(token, intendedVariant, 'gate_skipped', {
            gateReason: 'ios_gate_cooldown',
          });
          return true;
        }
        iosGateForVariantRef.current = intendedVariant;
        setIosGateVisible(true);
        markIosGateShown();
        // Critical: this used to be silent. The 0% opt-in investigation showed
        // iOS users on Safari (PWA not installed) were hitting this branch
        // and we logged NOTHING — looked like "prompt never fired" when in
        // reality we deferred to install-first.
        void postExtendedEvent(token, intendedVariant, 'ios_install_gate_shown', {
          gateReason: 'standalone_required_ios',
        });
        return true;
      }
      return false;
    },
    [token],
  );

  const showVariant = useCallback(
    async (
      v: PushPromptVariant,
      contextKey: string,
      meta: Record<string, unknown>,
      photo?: string | null,
      opts: { skipSessionStartGuard?: boolean } = {},
    ): Promise<void> => {
      if (!canShowAnyPrompt(v, contextKey, opts)) return;
      if (maybeShowIosGate(v)) return;
      setVariant(v);
      setContextMetadata(meta);
      setBlurredPhotoUrl(photo ?? null);
      setVisible(true);
      markPromptShown(contextKey);
      shownRef.current[v] = true;
      await logPromptEvent(token, v, 'shown', meta);
      refreshContext();
    },
    [canShowAnyPrompt, maybeShowIosGate, token, refreshContext],
  );

  const triggerVariantA = useCallback(async () => {
    if (!token) return;
    await refreshContext();
    const ctx = contextRef.current;
    if (!ctx || ctx.unseenLikesCount <= 0) {
      void postExtendedEvent(token, 'A_likes_blur', 'gate_skipped', {
        gateReason: 'no_unseen_likes',
      });
      return;
    }
    // Context key includes the unseen-likes count so a fresh like since the
    // last prompt counts as new context and bypasses the 5-min floor.
    const contextKey = `A:${ctx.unseenLikesCount}`;
    if (!canShowAnyPrompt('A_likes_blur', contextKey)) return;
    // Variant A REQUIRES a real liker photo to render the blurred preview.
    // If we can't fetch one, do not show A — fall through to B/C on next
    // eligible trigger.
    let photoUrl: string | null = null;
    try {
      const res = await fetch('/api/likes', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          photoUrl = data[0]?.primaryPhoto ?? null;
        }
      }
    } catch { /* ignore */ }
    if (!photoUrl) {
      void postExtendedEvent(token, 'A_likes_blur', 'gate_skipped', {
        gateReason: 'no_liker_photo_available',
      });
      return;
    }
    await showVariant(
      'A_likes_blur',
      contextKey,
      { unseen_likes_count: ctx.unseenLikesCount },
      photoUrl,
    );
  }, [token, canShowAnyPrompt, refreshContext, showVariant]);

  const triggerVariantB = useCallback(
    async (matchId?: number | string) => {
      if (!token) return;
      await refreshContext();
      const ctx = contextRef.current;
      // Must be the user's first ever match.
      if (!ctx || ctx.totalMatches !== 1) {
        void postExtendedEvent(token, 'B_first_match', 'gate_skipped', {
          gateReason: 'not_first_match',
          contextMetadata: { total_matches: ctx?.totalMatches ?? null },
        });
        return;
      }
      // Once-ever — first-match is by definition a one-time moment.
      if (ctx.lastShownByVariant.B_first_match) {
        void postExtendedEvent(token, 'B_first_match', 'gate_skipped', {
          gateReason: 'already_shown_once',
        });
        return;
      }
      // 2026-05 rebuild: dropped the cross-variant 24h gate (was the second
      // largest contributor to gate-skips). The 5-min context floor is the
      // sole cross-variant guard now.
      const contextKey = `B:${matchId ?? 'match'}`;
      if (!canShowAnyPrompt('B_first_match', contextKey)) return;
      await showVariant('B_first_match', contextKey, matchId != null ? { match_id: matchId } : {});
    },
    [token, canShowAnyPrompt, refreshContext, showVariant],
  );

  const triggerVariantC = useCallback(async () => {
    if (!token) return;
    await refreshContext();
    const ctx = contextRef.current;
    if (!ctx || ctx.profileViewsToday <= 0) {
      void postExtendedEvent(token, 'C_exit_intent', 'gate_skipped', {
        gateReason: 'no_profile_views_today',
      });
      return;
    }
    const contextKey = `C:${ctx.profileViewsToday}`;
    if (!canShowAnyPrompt('C_exit_intent', contextKey)) return;
    await showVariant('C_exit_intent', contextKey, { profile_views_today: ctx.profileViewsToday });
  }, [token, canShowAnyPrompt, refreshContext, showVariant]);

  // Variant E "First message": fires when the user lands on Messages with an
  // unread seed message. 2026-05 rebuild: dropped the strict "no other
  // variant has fired ever" gate — that asymmetric dedup was contributing 19
  // skips in 7 days for marginal benefit. The 5-min context floor handles
  // cross-variant spacing now.
  const triggerVariantE = useCallback(async () => {
    if (!token) return;
    await refreshContext();
    const ctx = contextRef.current;
    if (!ctx) {
      void postExtendedEvent(token, 'E_first_message', 'gate_skipped', {
        gateReason: 'context_unavailable',
      });
      return;
    }
    const contextKey = 'E:first';
    if (!canShowAnyPrompt('E_first_message', contextKey)) return;
    await showVariant('E_first_message', contextKey, {});
  }, [token, canShowAnyPrompt, refreshContext, showVariant]);

  // Variant D "Welcome": fallback for fresh accounts that have no likes /
  // matches / views yet. Guarantees a prompt surfaces at least once even when
  // A/B/C gates all fail on zero-state data.
  //
  // 2026-05-01 RESILIENCE FIX: previously, if `fetchBehavioralContext` failed
  // (5xx, network blip, anything), the entire D pipeline short-circuited and
  // the user got no prompt at all. D is the "guaranteed fallback" — it MUST
  // not depend on a network call to function. Now we proceed with safe
  // defaults (`{ swipesCount: 0, hasMatched: false, hasPushSubscription: false }`)
  // and log a `gate_skipped` event with `gateReason: 'context_fetch_failed'`
  // so we still know the upstream call broke.
  const triggerVariantD = useCallback(async () => {
    if (!token) return;
    // Session must be at least 15s old before this fallback fires.
    if (Date.now() - getSessionStartMs() < VARIANT_D_MIN_SESSION_MS) {
      void postExtendedEvent(token, 'D_welcome', 'gate_skipped', {
        gateReason: 'min_session_age_not_met_d',
      });
      return;
    }
    // Require onboarding complete + at least one photo so the account is
    // "real enough" to deserve a push prompt. Don't nudge people who haven't
    // finished signup.
    if (!user) {
      void postExtendedEvent(token, 'D_welcome', 'gate_skipped', {
        gateReason: 'no_user_loaded',
      });
      return;
    }
    const photos = (user as unknown as { photos?: Array<unknown> }).photos;
    const hasPhoto = Array.isArray(photos) && photos.length > 0;
    // 2026-05-01 Track 1 (push-funnel-fixes-2): use the typed `onboardingComplete`
    // field directly off `UserProfile` (generated from the OpenAPI spec — see
    // lib/api-client-react/src/generated/api.schemas.ts:73). Previously this
    // went through an unsafe `as { onboardingComplete?: boolean }` cast which
    // would silently break if the field shape changed. This gate exists to
    // stop Variant D firing for users mid-onboarding (11 users / 57 events
    // observed today on staging).
    const onboardingComplete = user.onboardingComplete === true;
    if (!hasPhoto) {
      void postExtendedEvent(token, 'D_welcome', 'gate_skipped', {
        gateReason: 'no_photo',
      });
      return;
    }
    if (!onboardingComplete) {
      void postExtendedEvent(token, 'D_welcome', 'gate_skipped', {
        gateReason: 'onboarding_incomplete',
      });
      return;
    }
    // Try to refresh context, but DO NOT bail if it fails — D is the fallback
    // of last resort. Track whether the fetch actually succeeded by snapshotting
    // the cached ref before/after.
    const before = contextRef.current;
    let contextFetchFailed = false;
    try {
      const ctx = await fetchBehavioralContext(token);
      if (ctx) {
        contextRef.current = ctx;
      } else {
        contextFetchFailed = true;
      }
    } catch {
      contextFetchFailed = true;
    }
    if (contextFetchFailed) {
      void postExtendedEvent(token, 'D_welcome', 'gate_skipped', {
        gateReason: 'context_fetch_failed',
        contextMetadata: { proceeded_with_defaults: true, had_prior_context: !!before },
      });
    }
    // Use either the freshly-loaded context or fall back to safe defaults.
    // We must still respect `hasPushSubscription` from cached context if we
    // have it — re-prompting a user who already subscribed is worse than
    // skipping when context is stale.
    const ctxOrDefault = contextRef.current ?? {
      unseenLikesCount: 0,
      profileViewsToday: 0,
      totalMatches: 0,
      hasPushSubscription: false,
      lastShownByVariant: {
        A_likes_blur: null,
        B_first_match: null,
        C_exit_intent: null,
        D_welcome: null,
        E_first_message: null,
        F_first_swipe: null,
      },
      lastDeclinedAt: null,
    } as BehavioralContext;
    if (ctxOrDefault.hasPushSubscription) {
      void postExtendedEvent(token, 'D_welcome', 'gate_skipped', {
        gateReason: 'has_active_subscription_late_check',
      });
      return;
    }
    const contextKey = 'D:welcome';
    if (!canShowAnyPrompt('D_welcome', contextKey)) return;
    await showVariant('D_welcome', contextKey, {});
  }, [token, user, canShowAnyPrompt, showVariant]);

  // Variant F "First swipe": fires ~1.5s after the user's very first swipe in
  // Discover (either direction). Unlike A/B/C/E it needs no likes/matches/views
  // context — the swipe is the signal. Bypasses the 10s session-start guard so
  // fast swipers are still chased; the 5-min cross-prompt floor still applies.
  const triggerVariantF = useCallback(
    async (direction: 'like' | 'pass') => {
      if (!token) return;
      await refreshContext();
      if (!user) {
        void postExtendedEvent(token, 'F_first_swipe', 'gate_skipped', {
          gateReason: 'no_user_loaded',
        });
        return;
      }
      // Mirror Variant D's "real enough account" gates: completed onboarding +
      // at least one photo. Don't chase users mid-signup.
      const photos = (user as unknown as { photos?: Array<unknown> }).photos;
      const hasPhoto = Array.isArray(photos) && photos.length > 0;
      if (!hasPhoto) {
        void postExtendedEvent(token, 'F_first_swipe', 'gate_skipped', {
          gateReason: 'no_photo',
        });
        return;
      }
      if (user.onboardingComplete !== true) {
        void postExtendedEvent(token, 'F_first_swipe', 'gate_skipped', {
          gateReason: 'onboarding_incomplete',
        });
        return;
      }
      // 'already opted in' / 'in-app browser' / 'permission denied' /
      // 'has active subscription' are all handled inside canShowAnyPrompt.
      const contextKey = 'F:first';
      if (!canShowAnyPrompt('F_first_swipe', contextKey, { skipSessionStartGuard: true })) return;
      await showVariant(
        'F_first_swipe',
        contextKey,
        { direction },
        null,
        { skipSessionStartGuard: true },
      );
    },
    [token, user, refreshContext, canShowAnyPrompt, showVariant],
  );

  const accept = useCallback(async () => {
    if (!token || !variant) return;
    setLoading(true);
    const meta = contextMetadata ?? {};
    try {
      await logPromptEvent(token, variant, 'accepted', meta);
      const result = await subscribeToPush(token);
      if (!result.success) {
        // Two diagnostic events when subscribe fails:
        //   1. `subscribe_failed` — captures EVERY failure with a `gateReason`
        //      pulled from pushPermissions' returned reason. This is the
        //      single biggest blind spot in the current funnel: we accept the
        //      prompt and then silently fail to subscribe and log nothing.
        //   2. `declined_permission` — kept for backward compat when the
        //      user denied at the OS level.
        void postExtendedEvent(token, variant, 'subscribe_failed', {
          gateReason: result.reason ?? 'unknown_failure',
          permissionState: readPermissionState(),
          contextMetadata: meta,
        });
        if (result.reason === 'permission_denied') {
          await logPromptEvent(token, variant, 'declined_permission', meta);
        }
      }
      try { await refreshUser?.(); } catch { /* ignore */ }
    } finally {
      setLoading(false);
      setVisible(false);
      setVariant(null);
      setBlurredPhotoUrl(null);
      setContextMetadata(null);
      refreshContext();
    }
  }, [token, variant, contextMetadata, refreshUser, refreshContext]);

  const decline = useCallback(async () => {
    // Guard against rapid taps firing multiple dismissed events while the
    // async logPromptEvent is in flight. Observed user 565 logging 3 events
    // in 580ms from repeated X-button taps before the modal unmounted.
    if (decliningRef.current) return;
    decliningRef.current = true;
    try {
      if (!token || !variant) {
        setVisible(false);
        setVariant(null);
        setBlurredPhotoUrl(null);
        setContextMetadata(null);
        return;
      }
      const meta = contextMetadata ?? {};
      try { await logPromptEvent(token, variant, 'dismissed', meta); } catch { /* ignore */ }
      setVisible(false);
      setVariant(null);
      setBlurredPhotoUrl(null);
      setContextMetadata(null);
    } finally {
      // Reset so the user can decline the NEXT variant fire in a later session.
      decliningRef.current = false;
    }
  }, [token, variant, contextMetadata]);

  const dismissIosGate = useCallback(() => {
    const v = iosGateForVariantRef.current ?? AMBIENT_VARIANT;
    iosGateForVariantRef.current = null;
    void postExtendedEvent(token, v, 'ios_install_gate_dismissed', {
      gateReason: 'user_closed_sheet',
    });
    setIosGateVisible(false);
  }, [token]);

  const showIosGuide = useCallback(() => {
    const v = iosGateForVariantRef.current ?? AMBIENT_VARIANT;
    iosGateForVariantRef.current = null;
    void postExtendedEvent(token, v, 'ios_install_gate_accepted', {
      gateReason: 'navigated_to_install_guide',
    });
    setIosGateVisible(false);
    if (typeof window !== 'undefined') {
      // 2026-05-12 — old /install-prompt page no longer has iOS install
      // instructions (it was repurposed as a push acceptance page). The new
      // /install-app page carries the platform-detected install steps and is
      // the correct destination for the iOS install guide flow.
      window.location.href = '/install-app';
    }
  }, [token]);

  return {
    visible,
    variant,
    loading,
    blurredPhotoUrl,
    iosGateVisible,
    contextMetadata,
    triggerVariantA,
    triggerVariantB,
    triggerVariantC,
    triggerVariantD,
    triggerVariantE,
    triggerVariantF,
    accept,
    decline,
    dismissIosGate,
    showIosGuide,
    refreshContext,
  };
}

// Module-singleton bus so unrelated components (Discover, MatchCelebration)
// can fire variants without prop drilling.
type Listener = (e: {
  type: 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
  matchId?: number | string;
  direction?: 'like' | 'pass';
}) => void;
const listeners = new Set<Listener>();
export function emitBehavioralPushTrigger(e: Parameters<Listener>[0]): void {
  listeners.forEach((l) => {
    try { l(e); } catch { /* ignore */ }
  });
}
export function onBehavioralPushTrigger(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}

// Variant copy
export const VARIANT_COPY: Record<PushPromptVariant, { title: string; subtitle: string; body?: string; cta: string }> = {
  A_likes_blur: {
    title: 'Someone liked you 💕',
    subtitle: 'Turn on notifications so you never miss her when she swipes right.',
    cta: 'Enable Notifications',
  },
  B_first_match: {
    title: 'You matched! 🔥',
    subtitle: 'Enable notifications to keep the conversation going.',
    cta: 'Enable Notifications',
  },
  C_exit_intent: {
    title: "🔥 Don't let your matches go cold",
    subtitle: '', // rendered dynamically with view count
    body: 'Enable notifications and be the first to reply.',
    cta: 'Enable Notifications',
  },
  D_welcome: {
    title: 'Stay in the loop 🔔',
    subtitle: 'Get instant alerts when a Latina likes you back or sends you a message.',
    cta: 'Enable Notifications',
  },
  E_first_message: {
    title: "Latinas reply fast here. Don't miss hers. 💬",
    subtitle: "She messaged you — turn on notifications so you don't miss her reply.",
    cta: 'YES, NOTIFY ME',
  },
  // Variant F static fallback. The title is resolved direction-adaptively at
  // render time by buildVariantTitle(); this static value is only used if the
  // variant somehow renders without a `direction` in its context metadata.
  F_first_swipe: {
    title: 'Get pinged the second she likes you back.',
    subtitle: '',
    cta: 'Enable Notifications',
  },
};

export function buildVariantSubtitle(
  variant: PushPromptVariant,
  meta: Record<string, unknown> | null,
): string {
  if (variant === 'C_exit_intent') {
    const n = Number(meta?.profile_views_today ?? 0);
    return `${n} ${n === 1 ? 'person' : 'people'} viewed your profile today`;
  }
  return VARIANT_COPY[variant].subtitle;
}

/**
 * Resolves the modal headline. Variant F is direction-adaptive — the title
 * carries the whole pitch (its subtitle is intentionally empty). All other
 * variants use their static VARIANT_COPY title.
 */
export function buildVariantTitle(
  variant: PushPromptVariant,
  meta: Record<string, unknown> | null,
): string {
  if (variant === 'F_first_swipe') {
    const direction = meta?.direction === 'pass' ? 'pass' : 'like';
    return firstSwipePushTitle(direction);
  }
  return VARIANT_COPY[variant].title;
}

// Right-swipe counter (session-scoped) for Variant A trigger
const SWIPE_COUNT_KEY = 'papacito_session_right_swipe_count';
export function bumpSessionRightSwipeCount(): number {
  try {
    const n = Number(sessionStorage.getItem(SWIPE_COUNT_KEY) || '0') + 1;
    sessionStorage.setItem(SWIPE_COUNT_KEY, String(n));
    return n;
  } catch { return 0; }
}
export function getSessionRightSwipeCount(): number {
  try { return Number(sessionStorage.getItem(SWIPE_COUNT_KEY) || '0'); } catch { return 0; }
}
